// Diccionarios de palabras para cada idioma
// Todas las palabras deben tener exactamente 5 letras y ser válidas

export interface Dictionary {
  language: string
  targetWords: string[]
  validWords: string[]
}

// Función para remover acentos de palabras españolas
export function removeAccents(word: string): string {
  return word
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
}

// Función para crear un diccionario sin acentos
export function createAccentFreeDictionary(originalWords: string[]): string[] {
  return originalWords.map(word => removeAccents(word))
}

// Importar los archivos JSON de diccionarios
import esCandidatesData from '@/data/dictionaries/es-candidates.json'
import esValidWordsData from '@/data/dictionaries/es-valid-words.json'

// Diccionario en español - SIN ACENTOS
export const spanishDictionary: Dictionary = {
  language: 'es',
  targetWords: createAccentFreeDictionary(esCandidatesData.words), // 1000 palabras más comunes para candidatas
  validWords: createAccentFreeDictionary(esValidWordsData.words)   // Todas las palabras válidas para validación
}

// Función para obtener el diccionario correcto según el idioma
// Nota: Solo soportamos español ahora que "adivina-la-palabra" es exclusivo de español
export function getDictionary(language: string): Dictionary {
  switch (language) {
    case 'es':
      return spanishDictionary
    default:
      return spanishDictionary
  }
}

// Función para obtener una fecha en formato YYYY-MM-DD
export function getDateString(date: Date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Función para obtener el número de días desde una fecha base (1 de enero de 2024)
export function getDaysSinceBase(date: Date = new Date()): number {
  // Crear fechas locales para evitar problemas de zona horaria
  const baseDate = new Date(2024, 0, 1) // 1 de enero de 2024 en zona horaria local
  const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()) // Solo fecha, sin hora

  // Calcular diferencia usando fechas locales sin conversión a UTC
  const baseYear = baseDate.getFullYear()
  const baseMonth = baseDate.getMonth()
  const baseDay = baseDate.getDate()

  const localYear = localDate.getFullYear()
  const localMonth = localDate.getMonth()
  const localDay = localDate.getDate()

  // Crear fechas UTC para el cálculo (esto evita problemas de zona horaria)
  const baseUTC = new Date(Date.UTC(baseYear, baseMonth, baseDay))
  const localUTC = new Date(Date.UTC(localYear, localMonth, localDay))

  const diffTime = localUTC.getTime() - baseUTC.getTime()
  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24))



  return days
}

// Función para generar un seed determinista basado en la fecha
export function generateDateSeed(date: Date = new Date()): number {
  const daysSinceBase = getDaysSinceBase(date)
  // Usar un hash simple para distribuir mejor las palabras
  return Math.abs(daysSinceBase * 9301 + 49297) % 65536
}

// Función para obtener una palabra consistente del día usando seed global
export function getDailyWord(language: string, date: Date = new Date()): string {
  const dictionary = getDictionary(language)
  const seed = generateDateSeed(date)

  // Usar el seed para obtener la misma palabra globalmente
  // Esto asegura que todos los usuarios vean la misma palabra en el mismo día
  const wordIndex = seed % dictionary.targetWords.length

  return dictionary.targetWords[wordIndex]
}

// Función para obtener la palabra de un día específico
export function getWordForDate(language: string, dateString: string): string {
  const date = new Date(dateString)
  return getDailyWord(language, date)
}

// Función para verificar si una fecha es válida para el juego
export function isValidGameDate(dateString: string): boolean {
  const date = new Date(dateString)
  const today = new Date()
  const daysSinceBase = getDaysSinceBase(date)

  // Solo permitir fechas desde la fecha base hasta hoy
  return daysSinceBase >= 0 && date <= today
}

// Función para obtener las próximas palabras (para preview)
export function getUpcomingWords(language: string, count: number = 7): Array<{ date: string, word: string }> {
  const result = []
  const today = new Date()

  for (let i = 1; i <= count; i++) {
    const futureDate = new Date(today)
    futureDate.setDate(today.getDate() + i)

    const dateString = getDateString(futureDate)
    const word = getDailyWord(language, futureDate)

    result.push({ date: dateString, word })
  }

  return result
}

// Función para verificar que el sistema de seed funciona correctamente
export function verifySeedConsistency(language: string, date: Date = new Date()): boolean {
  const word1 = getDailyWord(language, date)
  const word2 = getDailyWord(language, date)
  return word1 === word2
}

// Función para obtener información de debug del seed
export function getSeedInfo(language: string, date: Date = new Date()): {
  date: string
  daysSinceBase: number
  seed: number
  wordIndex: number
  word: string
  totalWords: number
} {
  const dictionary = getDictionary(language)
  const daysSinceBase = getDaysSinceBase(date)
  const seed = generateDateSeed(date)
  const wordIndex = seed % dictionary.targetWords.length
  const word = getDailyWord(language, date)

  return {
    date: getDateString(date),
    daysSinceBase,
    seed,
    wordIndex,
    word,
    totalWords: dictionary.targetWords.length
  }
}

// Función para obtener las palabras pasadas (para historial)
export function getPastWords(language: string, count: number = 7): Array<{ date: string, word: string }> {
  const result = []
  const today = new Date()

  for (let i = 1; i <= count; i++) {
    const pastDate = new Date(today)
    pastDate.setDate(today.getDate() - i)

    const dateString = getDateString(pastDate)
    const word = getDailyWord(language, pastDate)

    result.push({ date: dateString, word })
  }

  return result
}

// Función para obtener un rango de palabras (pasadas y futuras)
export function getWordRange(language: string, daysBack: number = 7, daysForward: number = 7): Array<{ date: string, word: string, isPast: boolean, isToday: boolean }> {
  const result = []
  const today = new Date()

  // Palabras pasadas
  for (let i = daysBack; i >= 1; i--) {
    const pastDate = new Date(today)
    pastDate.setDate(today.getDate() - i)

    const dateString = getDateString(pastDate)
    const word = getDailyWord(language, pastDate)

    result.push({
      date: dateString,
      word,
      isPast: true,
      isToday: false
    })
  }

  // Palabra de hoy
  const todayString = getDateString(today)
  const todayWord = getDailyWord(language, today)
  result.push({
    date: todayString,
    word: todayWord,
    isPast: false,
    isToday: true
  })

  // Palabras futuras
  for (let i = 1; i <= daysForward; i++) {
    const futureDate = new Date(today)
    futureDate.setDate(today.getDate() + i)

    const dateString = getDateString(futureDate)
    const word = getDailyWord(language, futureDate)

    result.push({
      date: dateString,
      word,
      isPast: false,
      isToday: false
    })
  }

  return result
} 